"use client";

import { useState, type ReactNode } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SessionExpiredError } from "@/lib/api-client/authenticatedClient";
import { extractApiErrorMessage } from "@/lib/request/extractApiErrorMessage";
import { reportError } from "@/lib/observability/reportError";
import { toast } from "@/lib/toast";

function handleError(error: unknown, meta?: Record<string, unknown>) {
  if (!(error instanceof SessionExpiredError)) {
    reportError(error, { source: "react-query" });
  }
  toast.error(
    extractApiErrorMessage(error, {
      showRawMessage: meta?.showRawApiError === true,
    }),
  );
}

export function ReactQueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => handleError(error, query.meta),
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) =>
            handleError(error, mutation.meta),
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}
